import * as graphics from "@client/app/graphics";
import {AppleHealthResyncCard} from "@client/components/AppleHealthResyncCard";
import {Gradient} from "@client/components/Chrome";
import {DateCarousel} from "@client/components/DateCarousel";
import {DayForm} from "@client/components/DayForm";
import {DaySummary, DaySummaryV2} from "@client/components/DaySummary";
import {Flush} from "@client/components/Flush";
import {HeaderSurface} from "@client/components/Glass";
import {INITIAL_WEIGHTS, WorkoutCard} from "@client/components/GuidedWorkout";
import {Steps, WeighIn} from "@client/components/journal";
import {JournalWorkout} from "@client/components/JournalWorkout";
import {ActivityCard, MealCard} from "@client/components/MealCard";
import {Streak} from "@client/components/Navigation";
import {Pill} from "@client/components/SimpleScene";
import {
    JOY_GRADIENT_TOP,
    migrateClass,
    primaryButtonClass,
    secondaryButtonClass,
} from "@client/components/styles";
import {TabBar} from "@client/components/TabBar";
import {TourCard} from "@client/components/TourCard";
import {WeekDatePicker} from "@client/components/WeekDatePicker";
import {Welcome} from "@client/components/Welcome";
import {processResponse} from "@client/components/Workout";
import {WorkoutBarSlot} from "@client/components/WorkoutBar";
import * as constants from "@client/constants";
import * as dates from "@client/dates";
import {type CaptureResult, useCapture} from "@client/features/camera";
import * as dialogs from "@client/features/dialogs";
import * as habits from "@client/features/habits";
import {TitleAndDescription} from "@client/features/habits";
import * as healthkit from "@client/features/healthkit";
import * as journal from "@client/features/journal";
import {notify} from "@client/features/notifications";
import * as orm from "@client/features/orm";
import * as push from "@client/features/push";
import * as recording from "@client/features/recording";
import {showFeelerIfFirstTime} from "@client/features/scenes";
import {shareLink} from "@client/features/sharing";
import * as workouts from "@client/features/workouts";
import * as forms from "@client/forms";
import type {Conditions} from "@client/gotterdammerung";
import {animateNavigation, settled} from "@client/gotterdammerung";
import * as hardware from "@client/hardware";
import {App} from "@client/hardware";
import {useTimeoutFn} from "@client/hooks";
import * as router from "@client/router";
import * as analytics from "@client/shared/analytics";
import * as typography from "@client/shared/typography";
import {store, useAppSelector} from "@client/store";
import * as utils from "@client/utils";
import {DragDropContext, Draggable, DropResult, Droppable} from "@hello-pangea/dnd";
import {ChevronRightIcon} from "@heroicons/react/16/solid";
import NumberFlow from "@number-flow/react";
import {classNames, reverse, server} from "@reactivated";
import {Decimal} from "decimal.js";
import {produce} from "immer";
import {AnimatePresence, motion} from "motion/react";
import * as React from "react";

import {useLearningWeek} from "./LearningWeek";
import {usePlanWaitlist} from "./PlanOnboarding";
import {isWalkingClubBoxed, useWalkingClubBox} from "./WalkingClub";

const styles = {
    spacer: "space-y-[15px]",
    migrate: migrateClass,
    empty: "journal-empty flex flex-1 flex-col items-center justify-center text-center",
} as const;

interface GratitudeProps {
    journal: server.journal.day.Journal.output;
    setJournal: (journal: server.journal.day.Journal.output) => void;
}

function Gratitude(props: GratitudeProps) {
    const gratitudeDialog = dialogs.useDialog<
        {gratitude: string | null},
        {label: string}
    >({
        Content: ({label, close}) => {
            const [gratitude, setGratitude] = React.useState(
                props.journal.day.gratitude,
            );
            const form = forms.useField(
                {kind: "textarea", name: "gratitude", label, nullable: true},
                {value: gratitude, onChange: setGratitude},
            );

            return (
                <form
                    className="min-w-[320px] space-y-4 p-6"
                    onSubmit={(event) => {
                        event.preventDefault();
                        close({gratitude});
                    }}
                >
                    <forms.Field field={form} />
                    <typography.Button submit>Submit</typography.Button>
                </form>
            );
        },
    });

    const assignment = props.journal.assignment;

    if (assignment?.habits.GRATITUDE == null) {
        return null;
    }

    const GRATITUDE = assignment.habits.GRATITUDE;

    return (
        <>
            {gratitudeDialog.dialog}
            <habits.JournalHabit
                habit={habits.habits.GRATITUDE}
                content={
                    <TitleAndDescription
                        title="Gratitude"
                        description="Today, I am grateful for..."
                    />
                }
                isComplete={(props.journal.day.gratitude?.trim() ?? "") !== ""}
                handler={{
                    onClick: async () => {
                        const result = await gratitudeDialog({label: GRATITUDE.prompt});

                        if (result != null) {
                            const journal = produce(props.journal, (draftState) => {
                                draftState.day.gratitude = result.gratitude;
                            });

                            props.setJournal(journal);

                            void server.journal.day.update_day(
                                {
                                    user_id: props.journal.day.user_id,
                                    key: props.journal.day.key,
                                },
                                {...journal.day, deleted_meal_uuids: []},
                            );
                        }
                    },
                }}
            />
        </>
    );
}

function getFollowOnInstagramVisibility(
    profile: server.core.rpc.Profile.output,
): "VISIBLE" | "POSTPONED" | "NEVER" {
    // Maybe a better way.
    // https://echotools.app/2021/directly-link-to-social-media-user-profile-in-swift-application/
    // Maybe a better way.
    // https://echotools.app/2021/directly-link-to-social-media-user-profile-in-swift-application/
    const force =
        (typeof localStorage != "undefined" &&
            localStorage.getItem("FORCE_FOLLOW_ON_INSTAGRAM") != null) ||
        false;

    if (force) {
        return "VISIBLE";
    }

    if (profile.entries_created < 20 && profile.workouts_completed < 5) {
        return "NEVER";
    }

    const settings = profile.settings.follow_on_instagram;

    if (settings?.status == "ENABLED") {
        return "NEVER";
    }

    if (settings?.status == "NEVER") {
        return "NEVER";
    }

    if (settings?.status == "LATER") {
        const postponedUntil = dates.parseISO(settings.postponed_until);
        if (dates.isBefore(new Date(), postponedUntil)) {
            return "POSTPONED";
        }
    }
    return "VISIBLE";
}

function getMyFitnessPalVisibility(
    profile: server.core.rpc.Profile.output,
): "VISIBLE" | "POSTPONED" | "NEVER" {
    const force =
        (typeof localStorage != "undefined" &&
            localStorage.getItem("FORCE_MYFITNESSPAL") != null) ||
        false;

    const settings = profile.settings.myfitnesspal;

    if (force) {
        return "VISIBLE";
    }

    if (
        !profile.user.casted_dismissed_announcements.includes("HAS_USED_MYFITNESSPAL")
    ) {
        return "NEVER"; // Or a more specific status if needed, but NEVER fits the existing logic
    }

    if (settings?.status == "ENABLED") {
        return "NEVER";
    }

    if (settings?.status == "NEVER") {
        return "NEVER";
    }

    if (settings?.status == "LATER") {
        const postponedUntil = dates.parseISO(settings.postponed_until);
        if (dates.isBefore(new Date(), postponedUntil)) {
            return "POSTPONED";
        }
    }
    return "VISIBLE";
}

function FollowOnInstagram(props: Props) {
    const INSTAGRAM_URL = "https://instagram.com/joyhealthtracker/";

    const settings = props.profile.settings.follow_on_instagram;

    const postponements = settings?.status == "LATER" ? settings.postponements : 0;
    const {dispatch} = props;

    const content = (
        <TitleAndDescription
            title="Follow us on Instagram"
            description="Get tips, workouts, recipes and more."
            actions={
                <>
                    <button
                        className={primaryButtonClass}
                        onClick={() => {
                            void analytics.track("CLICKED_FOLLOW_ON_INSTAGRAM");
                            window.open(INSTAGRAM_URL, "_blank", "noopener,noreferrer");

                            const updatedProfile = produce(props.profile, (draft) => {
                                draft.settings = {
                                    ...draft.settings,
                                    follow_on_instagram: {
                                        status: "ENABLED",
                                    },
                                };
                            });
                            dispatch({
                                type: "SET_PROFILE",
                                profile: updatedProfile,
                            });
                            void server.core.rpc.update_settings(
                                updatedProfile.settings,
                            );
                        }}
                    >
                        Follow
                    </button>
                    <button
                        onClick={() => {
                            void analytics.track("POSTPONED_FOLLOW_ON_INSTAGRAM", {
                                postponements,
                            });

                            const updatedProfile = produce(props.profile, (draft) => {
                                draft.settings = {
                                    ...draft.settings,
                                    follow_on_instagram: {
                                        status: "LATER",
                                        postponements: postponements + 1,
                                        postponed_until: dates.format(
                                            dates.addDays(
                                                new Date(),
                                                1 + postponements,
                                            ),
                                            "yyyy-MM-dd",
                                        ),
                                    },
                                };
                            });
                            dispatch({
                                type: "SET_PROFILE",
                                profile: updatedProfile,
                            });
                            void server.core.rpc.update_settings(
                                updatedProfile.settings,
                            );
                        }}
                        className={secondaryButtonClass}
                    >
                        Postpone
                    </button>
                    {postponements >= 3 && (
                        <button
                            onClick={() => {
                                void analytics.track("DISMISSED_FOLLOW_ON_INSTAGRAM", {
                                    postponements,
                                });

                                const updatedProfile = produce(
                                    props.profile,
                                    (draft) => {
                                        draft.settings = {
                                            ...draft.settings,
                                            follow_on_instagram: {
                                                status: "NEVER",
                                            },
                                        };
                                    },
                                );
                                dispatch({
                                    type: "SET_PROFILE",
                                    profile: updatedProfile,
                                });
                                void server.core.rpc.update_settings(
                                    updatedProfile.settings,
                                );
                            }}
                            className={classNames(secondaryButtonClass, "ml-7")}
                        >
                            Never
                        </button>
                    )}
                </>
            }
        />
    );

    return (
        <habits.JournalHabitShell
            label="Follow us on Instagram"
            habit={{
                name: "Follow us on Instagram",
                colors: "bg-red-100 text-red-800",
                icon: "custom-instagram",
                iconClass: "size-5!",
            }}
            content={content}
            isComplete={null}
        />
    );
}

function CheckIn(props: Props) {
    if (props.needsToCheckIn == false && props.showCheckIn == false) {
        return null;
    }

    const content = (
        <TitleAndDescription
            title={`Check in with ${props.profile.user.manager_name}`}
            description={props.needsToCheckIn ? "Tap to start" : "Tap to view"}
        />
    );

    return (
        <habits.JournalHabitShell
            label={`Check in with ${props.profile.user.manager_name}`}
            habit={habits.habits.CHECK_IN}
            content={content}
            isComplete={props.needsToCheckIn == false}
            onClick={() => {
                void router.push("check_in.index", props.params, props.resolved);
            }}
        />
    );
}

function MigrateFroMyFitnessPal(props: Props) {
    const settings = props.profile.settings.myfitnesspal;
    const postponements = settings?.status == "LATER" ? settings.postponements : 0;
    const {dispatch} = props;

    const title = (
        <div className="flex items-center">
            <span className="text-sm font-medium text-gray-950 hover:text-gray-600">
                Migrate from
            </span>
            <graphics.Mfp
                style={{color: "#0066EE"}}
                className="mt-0.5 ml-1 h-4 w-auto"
                title="MyFitnessPal"
            />
        </div>
    );

    const content = (
        <TitleAndDescription
            title={title}
            description="Import your journal, recipes and go-to foods"
            actions={
                <>
                    <button
                        className={primaryButtonClass}
                        onClick={() => {
                            void analytics.track("CLICKED_MIGRATE_FROM_MYFITNESSPAL");
                            void router.push("migrate.index", {});
                        }}
                    >
                        Start
                    </button>
                    <button
                        onClick={() => {
                            void analytics.track(
                                "POSTPONED_MIGRATE_FROM_MYFITNESSPAL",
                                {
                                    postponements,
                                },
                            );

                            const updatedProfile = produce(props.profile, (draft) => {
                                draft.settings = {
                                    ...draft.settings,
                                    myfitnesspal: {
                                        status: "LATER",
                                        postponements: postponements + 1,
                                        postponed_until: dates.format(
                                            dates.addDays(
                                                new Date(),
                                                1 + postponements,
                                            ),
                                            "yyyy-MM-dd",
                                        ),
                                    },
                                };
                            });
                            dispatch({
                                type: "SET_PROFILE",
                                profile: updatedProfile,
                            });
                            void server.core.rpc.update_settings(
                                updatedProfile.settings,
                            );
                        }}
                        className={secondaryButtonClass}
                    >
                        Postpone
                    </button>
                    <button
                        onClick={() => {
                            void analytics.track(
                                "DISMISSED_MIGRATE_FROM_MYFITNESSPAL",
                                {
                                    postponements,
                                },
                            );

                            const updatedProfile = produce(props.profile, (draft) => {
                                draft.settings = {
                                    ...draft.settings,
                                    myfitnesspal: {
                                        status: "NEVER",
                                    },
                                };
                            });
                            dispatch({
                                type: "SET_PROFILE",
                                profile: updatedProfile,
                            });
                            void server.core.rpc.update_settings(
                                updatedProfile.settings,
                            );
                        }}
                        className={classNames(secondaryButtonClass, "ml-7")}
                    >
                        Never
                    </button>
                </>
            }
        />
    );

    return (
        <habits.JournalHabitShell
            label="Migrate from MyFitnessPal"
            habit={{
                name: "Migrate from MyFitnessPal",
                colors: "bg-sky-100 text-sky-800",
                icon: "mfp-silhouette",
                iconClass: "size-[1.4rem]!",
            }}
            content={content}
            isComplete={null}
        />
    );
}

function Supplement(props: {
    journal: server.journal.day.Journal.output;
    setJournal: (journal: server.journal.day.Journal.output) => void;
    supplement: server.journal.day.Assignment.output["supplements"][number];
}) {
    const {journal, setJournal, supplement} = props;
    const allEntries = props.journal.day.meals.flatMap((meal) => meal.entries);
    const allSupplements = allEntries.filter(
        (entry) =>
            entry.backend == props.supplement.backend &&
            entry.backend_id == props.supplement.backend_id,
    );

    const total = allSupplements.reduce((acc, entry) => {
        const staged = entry;
        const nextUnit = props.supplement.unit;
        const units = entry.units;

        const previousQuantityMultiplier = units.find(
            (u) => u.unit == staged.unit,
        )!.multiplier;
        const currentQuantityMultiplier = units.find(
            (u) => u.unit == nextUnit,
        )!.multiplier;

        const currentQuantity = new Decimal(staged.quantity)
            .times(currentQuantityMultiplier)
            .dividedBy(previousQuantityMultiplier);

        return acc.add(currentQuantity);
    }, new Decimal(0));

    return (
        <habits.JournalHabit
            key={supplement.uuid}
            habit={{
                name: supplement.name,
                colors: "text-pink-900 bg-pink-100",
                icon: "0492-pills",
            }}
            content={
                <TitleAndDescription
                    title={supplement.name}
                    description={
                        <div className="flex items-baseline gap-1 text-sm font-light whitespace-nowrap text-gray-950">
                            <NumberFlow
                                value={total.toNumber()}
                                className="missing-tailwind-support-supplement text-lg text-gray-950"
                                suffix={` / ${new Decimal(props.supplement.target)
                                    .times(props.supplement.frequency)
                                    .toString()} ${utils.truncateUnit(props.supplement.unit, 110)}`}
                            />
                        </div>
                    }
                />
            }
            isComplete={total.greaterThanOrEqualTo(
                new Decimal(props.supplement.target).times(props.supplement.frequency),
            )}
            handler={{
                increment: () => {
                    hardware.haptic();

                    const updatedJournal = produce(journal, (draft) => {
                        let suppplementsMeal = draft.day.meals.find(
                            (meal) => meal.name == "__SUPPLEMENTS__",
                        );

                        if (suppplementsMeal == null) {
                            suppplementsMeal = {
                                uuid: crypto.randomUUID(),
                                share_uuid: "",
                                key: draft.day.key,
                                recreate_recipe: null,
                                name: "__SUPPLEMENTS__",
                                hour: null,
                                entries: [],
                                type: "AUTO_CREATED",
                            };
                            draft.day.meals.push(suppplementsMeal);
                        }
                        const entry = journal.supplements.find(
                            (entry) =>
                                entry.backend_id == supplement.backend_id &&
                                entry.backend == supplement.backend,
                        );
                        if (entry != null) {
                            suppplementsMeal.entries.push({
                                ...entry.entry,
                                uuid: crypto.randomUUID(),
                                quantity: supplement.target,
                                unit: supplement.unit,
                            });
                        }
                    });

                    setJournal(updatedJournal);
                    void server.journal.day.update_day(
                        {
                            user_id: updatedJournal.day.user_id,
                            key: updatedJournal.day.key,
                        },
                        {...updatedJournal.day, deleted_meal_uuids: []},
                    );
                },
                decrement: () => {
                    hardware.haptic();

                    const suppplementsMeal = journal.day.meals.find(
                        (meal) => meal.name == "__SUPPLEMENTS__",
                    );

                    const existingSupplement = suppplementsMeal?.entries.find(
                        (entry) =>
                            entry.backend == supplement.backend &&
                            entry.backend_id == supplement.backend_id,
                    );

                    if (suppplementsMeal == null || existingSupplement == null) {
                        return;
                    }

                    const updatedJournal = produce(journal, (draft) => {
                        draft.day.meals.forEach((meal) => {
                            meal.entries = meal.entries.filter(
                                (entry) => entry.uuid != existingSupplement.uuid,
                            );
                        });
                    });

                    setJournal(updatedJournal);

                    void server.journal.nutrition.delete_entry({
                        form: existingSupplement.uuid,
                    });
                },
            }}
        />
    );
}

// A supplement that was logged (e.g. a peptide dose) but isn't on a coach's
// assignment. Same card as the coach supplement, minus the "/ target" denominator.
function LoggedSupplement(props: {
    journal: server.journal.day.Journal.output;
    setJournal: (journal: server.journal.day.Journal.output) => void;
    entries: server.journal.nutrition.Entry.output[];
}) {
    const {journal, setJournal, entries} = props;
    const first = entries[0];

    const total = entries.reduce((acc, entry) => {
        const fromMultiplier = entry.units.find(
            (u) => u.unit == entry.unit,
        )!.multiplier;
        const toMultiplier = entry.units.find((u) => u.unit == first.unit)!.multiplier;
        return acc.add(
            new Decimal(entry.quantity).times(toMultiplier).dividedBy(fromMultiplier),
        );
    }, new Decimal(0));

    const last = entries[entries.length - 1];

    return (
        <habits.JournalHabit
            habit={{
                name: first.name,
                colors: "text-pink-900 bg-pink-100",
                icon: "0492-pills",
            }}
            content={
                <TitleAndDescription
                    title={first.name}
                    description={
                        <div className="flex items-baseline gap-1 text-sm font-light whitespace-nowrap text-gray-950">
                            <NumberFlow
                                value={total.toNumber()}
                                className="missing-tailwind-support-supplement text-lg text-gray-950"
                                suffix={` ${utils.truncateUnit(first.unit, 110)}`}
                            />
                        </div>
                    }
                />
            }
            isComplete={true}
            handler={{
                increment: () => {
                    hardware.haptic();

                    const updatedJournal = produce(journal, (draft) => {
                        const meal = draft.day.meals.find(
                            (m) => m.name == "__SUPPLEMENTS__",
                        );
                        const template = meal?.entries.find((e) => e.uuid == last.uuid);
                        if (meal == null || template == null) {
                            return;
                        }
                        meal.entries.push({...template, uuid: crypto.randomUUID()});
                    });

                    setJournal(updatedJournal);
                    void server.journal.day.update_day(
                        {
                            user_id: updatedJournal.day.user_id,
                            key: updatedJournal.day.key,
                        },
                        {...updatedJournal.day, deleted_meal_uuids: []},
                    );
                },
                decrement: () => {
                    hardware.haptic();

                    const updatedJournal = produce(journal, (draft) => {
                        draft.day.meals.forEach((meal) => {
                            meal.entries = meal.entries.filter(
                                (e) => e.uuid != last.uuid,
                            );
                        });
                    });

                    setJournal(updatedJournal);
                    void server.journal.nutrition.delete_entry({
                        form: last.uuid,
                    });
                },
            }}
        />
    );
}

// Consumers only: managed clients get their targets from their coach.
function Onboarding() {
    return (
        <button
            type="button"
            onClick={() => {
                void hardware.haptic();
                void router.push("macros.index", {});
            }}
            className="flex w-full items-center gap-3 rounded-[14px] bg-white/55 px-4 py-3 text-left shadow-[inset_0_0_0_0.5px_rgba(255,255,255,0.9)] active:bg-white"
        >
            <div className="flex-1">
                <div className="text-[13.5px] font-medium">Update your goals</div>
                <div className="text-[13px] font-light text-[oklch(0.5_0.03_280)]">
                    Recalculate your macros.
                </div>
            </div>
            <ChevronRightIcon className="size-4 text-[oklch(0.55_0.03_280)]" />
        </button>
    );
}

export function StepsAndWeight(props: {
    isMealPlan: boolean;
    profile: server.core.rpc.Profile.output;
    journal: server.journal.day.Journal.output;
    setJournal: (journal: server.journal.day.Journal.output) => void;
}) {
    if (props.isMealPlan != false) {
        return null;
    }
    return (
        <>
            <div className="flex gap-2.5">
                <div className="flex-1 *:h-full">
                    <Steps
                        profile={props.profile}
                        journal={props.journal}
                        setJournal={props.setJournal}
                    />
                </div>
                <div className="flex-1 *:h-full">
                    <WeighIn
                        profile={props.profile}
                        journal={props.journal}
                        setJournal={props.setJournal}
                    />
                </div>
            </div>
            <div className="mt-1.5 empty:mt-0">
                <Gratitude {...props} />
            </div>
        </>
    );
}

export function Habits(props: {
    children?: React.ReactNode;
    isMealPlan: boolean;
    profile: server.core.rpc.Profile.output;
    journal: server.journal.day.Journal.output;
    setJournal: (journal: server.journal.day.Journal.output) => void;
}) {
    const {journal, setJournal} = props;
    const {assignment, day} = journal;
    const data = useAppSelector((state) => orm.getMetrics(state, journal));

    // Supplements logged outside a coach assignment (e.g. peptide doses), grouped by
    // food so repeated doses of the same thing share one card.
    const assignedSupplementKeys = new Set(
        (assignment?.supplements ?? []).map((s) => `${s.backend}:${s.backend_id}`),
    );
    const loggedSupplementGroups = Object.values(
        (day.meals.find((meal) => meal.name == "__SUPPLEMENTS__")?.entries ?? [])
            .filter(
                (entry) =>
                    !assignedSupplementKeys.has(
                        `${entry.backend}:${entry.backend_id ?? ""}`,
                    ),
            )
            .reduce<Record<string, (typeof day.meals)[number]["entries"]>>(
                (groups, entry) => {
                    const key =
                        entry.backend_id != null
                            ? `${entry.backend}:${entry.backend_id}`
                            : `inline:${entry.name}`;
                    (groups[key] ??= []).push(entry);
                    return groups;
                },
                {},
            ),
    );

    return (
        <div className="mb-2.5 grid grid-cols-1 gap-2.5 empty:mb-0 md:grid-cols-2 md:*:only:col-span-2">
            {props.children}
            {(data.SLEEP != null || assignment?.habits.SLEEP != null) && (
                <habits.JournalHabit
                    habit={habits.habits.SLEEP}
                    content={
                        <TitleAndDescription
                            title="Sleep"
                            description={
                                <div className="flex items-baseline gap-1 text-sm font-light whitespace-nowrap text-gray-950">
                                    <span className="text-lg text-gray-950">
                                        {utils.convertSecondsToHoursMinutes(
                                            data.SLEEP ?? 0,
                                        )}
                                        {assignment?.habits.SLEEP != null && (
                                            <span className="text-sm">
                                                {` / ${utils.convertSecondsToHoursMinutes(assignment.habits.SLEEP)}`}
                                            </span>
                                        )}
                                    </span>
                                </div>
                            }
                        />
                    }
                    isComplete={
                        assignment?.habits.SLEEP == null
                            ? null
                            : (data.SLEEP ?? 0) >= assignment.habits.SLEEP
                    }
                    handler={{
                        decrement: () => {
                            hardware.haptic();
                            const updatedMetric = {
                                value: Math.max((data.SLEEP ?? 0) - 1800, 0),
                                start_date: day.key,
                                end_date: day.key,
                                timezone: dates.deviceTimezone(),
                                is_manual: true,
                                apple_health_uuids: [],
                                human: "",
                            };

                            setJournal(
                                produce(journal, (draft) => {
                                    draft.metrics.SLEEP = updatedMetric;
                                }),
                            );

                            void server.journal.day.update_metric_aggregates({
                                user_id: journal.day.user_id,
                                metrics: {
                                    [day.key]: {
                                        SLEEP: updatedMetric,
                                    },
                                },
                                workouts: {},
                            });
                        },
                        increment: () => {
                            hardware.haptic();
                            const updatedMetric = {
                                value: Math.max((data.SLEEP ?? 0) + 1800, 0),
                                start_date: day.key,
                                end_date: day.key,
                                timezone: dates.deviceTimezone(),
                                is_manual: true,
                                apple_health_uuids: [],
                                human: "",
                            };

                            setJournal(
                                produce(journal, (draft) => {
                                    draft.metrics.SLEEP = updatedMetric;
                                }),
                            );

                            void server.journal.day.update_metric_aggregates({
                                user_id: journal.day.user_id,
                                metrics: {
                                    [day.key]: {
                                        SLEEP: updatedMetric,
                                    },
                                },
                                workouts: {},
                            });
                        },
                    }}
                />
            )}
            {(data.WATER != null || assignment?.habits.WATER?.target != null) && (
                <habits.JournalHabit
                    habit={habits.habits.WATER}
                    content={
                        <TitleAndDescription
                            title="Drink water"
                            description={
                                <div className="flex items-baseline gap-1 text-sm font-light whitespace-nowrap text-gray-950">
                                    <NumberFlow
                                        value={data.WATER ?? 0}
                                        className="missing-tailwind-support-water text-lg text-gray-950"
                                        suffix={
                                            assignment?.habits.WATER?.target != null
                                                ? ` / ${assignment.habits.WATER.target} ${assignment.habits.WATER.unit ?? "cups"}`
                                                : ` ${assignment?.habits.WATER?.unit ?? "cups"}`
                                        }
                                    />
                                </div>
                            }
                        />
                    }
                    isComplete={
                        assignment?.habits.WATER?.target == null
                            ? null
                            : (data.WATER ?? 0) >= assignment.habits.WATER.target
                    }
                    handler={{
                        decrement: () => {
                            hardware.haptic();
                            const unit = assignment?.habits.WATER?.unit;
                            const step = unit === "oz" ? 8 : 1;
                            const updatedMetric = {
                                value: Math.max((data.WATER ?? 0) - step, 0),
                                start_date: day.key,
                                end_date: day.key,
                                timezone: dates.deviceTimezone(),
                                is_manual: true,
                                apple_health_uuids: [],
                                human: "",
                            };

                            setJournal(
                                produce(journal, (draft) => {
                                    draft.metrics.WATER = updatedMetric;
                                }),
                            );

                            void server.journal.day.update_metric_aggregates({
                                user_id: journal.day.user_id,
                                metrics: {
                                    [day.key]: {
                                        WATER: updatedMetric,
                                    },
                                },
                                workouts: {},
                            });
                        },
                        increment: () => {
                            hardware.haptic();
                            const unit = assignment?.habits.WATER?.unit;
                            const step = unit === "oz" ? 8 : 1;
                            const updatedMetric = {
                                value: Math.max((data.WATER ?? 0) + step, 0),
                                start_date: day.key,
                                end_date: day.key,
                                timezone: dates.deviceTimezone(),
                                is_manual: true,
                                apple_health_uuids: [],
                                human: "",
                            };

                            setJournal(
                                produce(journal, (draft) => {
                                    draft.metrics.WATER = updatedMetric;
                                }),
                            );

                            void server.journal.day.update_metric_aggregates({
                                user_id: journal.day.user_id,
                                metrics: {
                                    [day.key]: {
                                        WATER: updatedMetric,
                                    },
                                },
                                workouts: {},
                            });
                        },
                    }}
                />
            )}
            {assignment?.supplements != null && assignment.supplements.length > 0 && (
                <>
                    {assignment.supplements.map((supplement) => (
                        <Supplement
                            key={supplement.uuid}
                            supplement={supplement}
                            journal={journal}
                            setJournal={setJournal}
                        />
                    ))}
                </>
            )}
            {loggedSupplementGroups.map((entries) => (
                <LoggedSupplement
                    key={entries[0].uuid}
                    journal={journal}
                    setJournal={setJournal}
                    entries={entries}
                />
            ))}
        </div>
    );
}

export const buildCalendar = (day: server.journal.nutrition.Day.output) => {
    // Note: we do *not* use parseISO because 198701 will be parsed as a date instead of a meal plan ID.
    const date = dates.parse(day.key, "yyyy-MM-dd", new Date());
    const isMealPlan = isNaN(date.getTime());

    if (isMealPlan == true) {
        return {
            title: day.name ?? "Untitled plan",
            calendar: null,
        };
    }
    const dateString = dates.formatISO(date, {representation: "date"});
    const next = dates.formatISO(dates.addDays(date, 1), {
        representation: "date",
    });
    const previous = dates.formatISO(dates.subDays(date, 1), {
        representation: "date",
    });

    return {
        title: utils.formatDate(day.key),
        calendar: {
            formattedDay: utils.formatDay(day.key),
            todayString: dates.formatISO(new Date(), {representation: "date"}),
            date,
            dateString,
            next,
            previous,
        },
    };
};

const BottomSection = (props: Props) => {
    const {
        start,
        handlePhoto,
        resolveBarcode,
        dialog: captureDialog,
    } = useCapture({
        target: {type: "DAY", day: props.resolved.journal.day},
        profile: props.profile,
    });

    const addEntries = (
        entries: CaptureResult & {type: "entries"},
        journalMode: "SEARCH" | "BARCODE",
    ) => {
        if (entries.method === "cupboard") {
            journal.addEntriesToCupboard({
                entries: entries.entries,
                dispatch: props.dispatch,
            });
        } else {
            const {updatedJournal, entryUUIDs} = journal.addEntriesToJournal({
                journal: props.resolved.journal,
                meal: entries.meal,
                entries: entries.entries,
                journal_mode: journalMode,
                dispatch: props.dispatch,
                updated: (updatedStreak) => {
                    props.setJournal((prev) => ({
                        ...prev,
                        streak: updatedStreak,
                    }));
                },
            });
            const lastEntryUUID = entryUUIDs.at(-1);
            if (lastEntryUUID != null) {
                setTimeout(() => {
                    document
                        .getElementById(lastEntryUUID)
                        ?.scrollIntoView({behavior: "smooth"});
                }, 250);
            }
            props.dispatch(showFeelerIfFirstTime());
            props.setJournal(() => updatedJournal);
        }
    };

    const handleCaptureResult = async (result: CaptureResult) => {
        switch (result.type) {
            case "meal_image":
            case "facts_image":
                handlePhoto(
                    result.prompt,
                    result.type === "meal_image" ? "meal" : "facts",
                    {
                        currentJournal: props.resolved.journal,
                        setJournal: props.setJournal,
                    },
                );
                break;
            case "barcode": {
                const resolved = await resolveBarcode(result.barcode);
                if (resolved != null) {
                    addEntries({type: "entries", ...resolved}, "BARCODE");
                }
                break;
            }
            case "entries":
                addEntries(result, "SEARCH");
                break;
            case "canceled":
                break;
        }
    };

    const isScanning = useAppSelector((state) => state.search.isScanning);

    if (props.router.route == "workout") {
        return null;
    }

    if (props.currentTab != "journal") {
        return null;
    }

    if (props.meal_plan != null) {
        return null;
    }

    if (isScanning) {
        return null;
    }

    return (
        <div className="flex flex-col gap-joy-step">
            {captureDialog}
            <div className="flex justify-center">
                <Pill
                    action={{
                        icon: "custom-thick-plus",
                        title: "Log your food",
                        onClick: async () => {
                            const result = await start({initialView: "manual"});
                            await handleCaptureResult(result);
                        },
                    }}
                />
            </div>
            {props.profile.has_hidden_macros == true ? null : (
                <div className="space-y-2">
                    <DaySummary
                        onClick={
                            props.calendar != null
                                ? () => {
                                      void hardware.haptic();
                                      void props.switchTab("summary");
                                  }
                                : undefined
                        }
                        useKilojoules={props.useKilojoules}
                        target={props.day.target}
                        day={props.day}
                    />
                    <DaySummaryV2
                        onClick={
                            props.calendar != null
                                ? () => {
                                      void hardware.haptic();
                                      void props.switchTab("summary");
                                  }
                                : undefined
                        }
                        useKilojoules={props.useKilojoules}
                        target={props.day.target}
                        day={props.day}
                    />
                </div>
            )}
        </div>
    );
};

export type Props = ReturnType<typeof useJournalHooks>;

// -1 once the rest is over.
const restSecondsLeft = (end: Date | null, now: Date) =>
    end == null
        ? null
        : Math.max(dates.differenceInSeconds(end, now, {roundingMethod: "round"}), -1);

function useJournalHooks(
    props: Parameters<Parameters<typeof router.Journal.withHooks>[0]>[0],
) {
    const intervalRef = React.useRef<NodeJS.Timeout | null>(null);
    const endTimeRef = React.useRef<Date | null>(null);
    const [currentTime, setCurrentTime] = React.useState<Date>(new Date());
    const [route, setRoute] = React.useState<"workout" | "journal">("journal");
    const day = props.resolved.journal.day;
    const extra = buildCalendar(day);
    const meal_plan = props.resolved.journal.meal_plan;
    const manager_name = props.profile.user.manager_name;
    const activities = useAppSelector((state) =>
        orm.getActivities(state, props.resolved.journal),
    );
    const hasWorkout = props.resolved.journal.workout != null;

    // Once the slide that brought this day in is over: fetch the workout catalog
    // and sync HealthKit.
    React.useEffect(() => {
        void settled().then(() => {
            if (hasWorkout) {
                void props.dispatch(workouts.loadWorkoutData());
            }
            void healthkit.checkAndSyncHealthData({
                profile: props.profile,
                trace: "journal-loading",
            });
        });
    }, [day.key]);

    const startRestTimer = (duration: number | null) => {
        endTimeRef.current = null;

        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        const now = new Date();
        setCurrentTime(now);

        if (duration == null) {
            // Clear the timer on the Swift side when duration is null
            void hardware.updateLiveActivity({restSeconds: null});
            return;
        }

        endTimeRef.current = dates.addSeconds(new Date(), duration);

        void hardware.updateLiveActivity({restSeconds: duration});

        const interval = setInterval(() => {
            const tick = new Date();
            setCurrentTime(tick);
            if (restSecondsLeft(endTimeRef.current, tick) === -1) {
                clearInterval(interval);
                intervalRef.current = null;
            }
        }, 1000);
        intervalRef.current = interval;
    };

    // Handle rest timer background/foreground transitions
    React.useEffect(() => {
        const listener = App.addListener("appStateChange", (appState) => {
            const status = appState.isActive ? "active" : "inactive";

            if (status === "active") {
                setCurrentTime(new Date());
            }
        });

        return () => {
            void (async () => {
                void (await listener).remove();
            })();
        };
    }, []);

    // Cleanup on component unmount
    React.useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, []);

    const restTimer = restSecondsLeft(endTimeRef.current, currentTime);

    // Add hasNoMeals variable to check if there are no meals excluding supplements
    const hasNoMeals =
        day.meals
            .filter((meal) => meal.name !== "__SUPPLEMENTS__")
            .flatMap((meal) => meal.entries).length === 0 && activities.length === 0;

    const {status: pushStatus, register: registerPushNotifications} =
        push.usePushNotifications({profile: props.profile});

    const ratingUiVisible = useAppSelector(
        (state) => state.scenes.isAskingForRating !== false,
    );

    const shouldPrompt =
        pushStatus == "prompt" &&
        props.platform == "ios" &&
        !ratingUiVisible &&
        ((props.profile.user.is_client && props.resolved.journal.program != null) ||
            (day.meals.length > 0 && props.profile.entries_created > 3));

    React.useEffect(() => {
        if (shouldPrompt) {
            void analytics.track("PROMPTED_PUSH_NOTIFICATIONS");
        }
    }, [shouldPrompt]);

    React.useEffect(() => {
        window.addEventListener(
            "joy.prompt_updated",
            (event: CustomEventInit<server.journal.nutrition.ExpandedEntry.output>) => {
                const updatedEntry = event.detail;

                if (updatedEntry == null) {
                    return;
                }

                props.setResolved((prevState) =>
                    produce(prevState, (draftState) => {
                        for (const meal of draftState.journal.day.meals) {
                            meal.entries.forEach((entry, index) => {
                                if (entry.uuid == updatedEntry.uuid) {
                                    meal.entries[index] = updatedEntry;
                                }
                            });
                        }
                    }),
                );
            },
        );
    }, []);

    // Gone once its card has collapsed, not once iOS has an answer: the header
    // closes its slot right after the card instead of seconds later.
    const [pushPromptDismissed, setPushPromptDismissed] = React.useState(false);

    const handleNotifications = async () => {
        setPushPromptDismissed(true);
        void hardware.haptic();
        await registerPushNotifications();
    };

    const pushPrompt =
        shouldPrompt && !pushPromptDismissed ? (
            <TourCard
                title="Enable notifications"
                label="Enable"
                onDismiss={() => void handleNotifications()}
            >
                {manager_name != null ? `Updates from ${manager_name}.` : "Updates."}
                <br />
                Reports.
                <br />
                Sync across devices.
            </TourCard>
        ) : null;

    const {needsToCheckIn, showCheckIn} =
        props.resolved.journal.check_in != null
            ? {
                  needsToCheckIn:
                      props.resolved.journal.check_in.existing == null &&
                      props.resolved.journal.meal_plan == null,
                  showCheckIn: true,
              }
            : {needsToCheckIn: false, showCheckIn: false};

    React.useEffect(() => {
        if (props.resolved.journal.meal_plan != null) {
            const dismiss = props.notify(
                `Your manager has provided you with a meal plan for this day.

You can accept it and then modify it. Or clear it and add your own entries.`,
                {type: "info", permanent: true},
            );

            return () => {
                dismiss();
            };
        }
    }, [props.params.key, props.resolved.journal.meal_plan]);

    /*
    const dateWhenLoaded = React.useRef(props.params.key);

    React.useEffect(() => {
        const listener = App.addListener("appStateChange", (state) => {
            const today = dates.formatISO(new Date(), {representation: "date"});

            if (
                window.cordova != null &&
                extra.calendar != null &&
                state.isActive === true &&
                props.params.key === dateWhenLoaded.current &&
                dates.isBefore(extra.calendar.date, new Date()) === true
            ) {
                void analytics.track("SWITCHED_TO_TODAY", {
                    from_date: props.params.key,
                    to_date: today,
                });
                router.push("journal.journal", {key: today});
            }
        });

        return () => {
            void (async () => {
                void (await listener).remove();
            })();
        };
    }, [props.params.key]);
    */

    const targetOptions: [string | null, string][] = [
        [null, "---------"],
        ...props.resolved.journal.targets.map(
            (target) => [target.pk.toString(), target.name] as [string, string],
        ),
    ];

    const pForm = forms.useForm(
        {
            fields: {
                notes: {kind: "textarea", label: "Notes", nullable: true},
                include_in_reports: {kind: "boolean", label: "Include in reports"},
                target: {
                    kind: "select",
                    nullable: true,
                    widget: "select",
                    required: false,
                    options: targetOptions,
                },
            },
        },
        {
            value: {
                include_in_reports: day.include_in_reports,
                target: day.target?.pk.toString() ?? null,
                notes: day.notes,
            },
            onChange: (values) => {
                props.setResolved((prevState) => ({
                    journal: produce(prevState.journal, (draftState) => {
                        draftState.day.include_in_reports = values.include_in_reports;
                        draftState.day.target =
                            props.resolved.journal.targets.find(
                                (target) => target.pk.toString() === values.target,
                            ) ?? null;
                        draftState.day.notes = values.notes;
                    }),
                }));
            },
        },
    );

    // The bar's menu closes before the slide: an open menu and its scrim would be
    // in the slide's picture of the outgoing screen.
    const deleteWorkout = async (menuClosed: Promise<void> = Promise.resolve()) => {
        const exercises = (props.resolved.journal.workout?.sections ?? [])
            .flatMap((section) => section.groups)
            .flatMap((group) => group.exercises);

        // A session still running here would finalize into a deleted exercise.
        const session = recording.getSession(store.getState());
        if (
            (session.kind === "running" || session.kind === "paused") &&
            exercises.some((exercise) => exercise.uuid === session.exerciseUuid)
        ) {
            store.dispatch(notify("End the session before deleting", {type: "error"}));
            return false;
        }

        // The workout_uuid is not explicitly passed as the new endpoint works on day/user.
        // If a workout must exist for this action to be valid, UI should control button availability.
        void analytics.track("DELETED_WORKOUT");
        // The rest clear first: arriving after the end, it makes a store build before
        // #336 adopt the ending activity again.
        startRestTimer(null);
        void hardware.endLiveActivity();

        for (const exercise of exercises) {
            if (exercise.recording != null) {
                void hardware.discardWorkout(exercise.recording.apple_health_uuid);
            }
        }

        const response = await processResponse(
            server.journal.day.delete_journal_workout({
                key: props.resolved.journal.day.key,
                user_id: props.resolved.journal.day.user_id,
            }),
        );

        if (response.type == "success") {
            await menuClosed;
            await animateNavigation("back", () => {
                props.setResolved(() => ({
                    journal: response.data, // delete_journal_workout returns the full updated journal
                }));
                void props.switchTab("journal");
            });
        }
        // processResponse handles error notifications
        return response.type == "success";
    };

    const [workoutRoute, setWorkoutRoute] = React.useState<"custom" | "program">(
        "custom",
    );

    const isImperial = forms.useIsImperial();

    const createWorkout = async (source: "BLANK" | UUID) => {
        const response = await server.journal.workouts.select_program_workout({
            source_workout: source,
            user_id: props.resolved.journal.day.user_id,
            date: props.resolved.journal.day.key,
        });
        const isFemale = props.profile.gender == "f";

        if (response.type == "success") {
            if (response.data == "SUBSCRIPTION_REQUIRED") {
                props.dispatch({type: "NEEDS_SUBSCRIPTION"});
                void router.push("profile.subscription", {});
                return;
            }
            const workout = response.data;

            void hardware.startLiveActivity({
                workout: workout,
            });

            const workoutWithInitialWeights = produce(response.data, (draft) => {
                if (draft.mode == "NORMAL") {
                    return;
                }
                draft.sections.forEach((section) => {
                    section.groups.forEach((group) => {
                        group.exercises.forEach((exercise) => {
                            exercise.sets.forEach((set, index) => {
                                const initialValue = [
                                    INITIAL_WEIGHTS[
                                        exercise.type
                                            .name as keyof typeof INITIAL_WEIGHTS
                                    ],
                                    null,
                                ][0]?.initialLbs;

                                if (initialValue == null) {
                                    return;
                                }

                                const initialWeight = utils.roundNumberToNearest(
                                    new Decimal(initialValue).times(
                                        isFemale ? "0.5" : "1",
                                    ),
                                    new Decimal("2.5"),
                                );
                                const initialWeightKg =
                                    initialWeight.times("0.45359237");
                                const rounded = isImperial
                                    ? initialWeightKg.toString()
                                    : utils
                                          .roundNumberToNearest(
                                              initialWeightKg,
                                              new Decimal("2.5"),
                                          )
                                          .toString();

                                if (exercise.mode == "GUIDED" && index == 0) {
                                    set.weight = rounded;
                                    set.reps = null;
                                } else if (exercise.mode == "GUIDED") {
                                    set.reps = null;
                                } else {
                                    set.reps =
                                        INITIAL_WEIGHTS[
                                            exercise.type
                                                .name as keyof typeof INITIAL_WEIGHTS
                                        ].reps;
                                }
                            });
                        });
                    });
                });
            });
            await animateNavigation("start", () => {
                props.setResolved((prevState) => ({
                    journal: produce(prevState.journal, (draft) => {
                        draft.workout = workoutWithInitialWeights;
                    }),
                }));
                void props.switchTab("workout");
            });
        }
    };

    return {
        ...props,
        ...extra,
        workoutRoute,
        setWorkoutRoute,
        pForm,
        deleteWorkout,
        needsToCheckIn,
        showCheckIn,
        day,
        meal_plan,
        activities,
        showWeighIn: props.profile.user.is_reports_enabled === true,
        useKilojoules: props.profile.use_kilojoules,
        pushPrompt,
        hasNoMeals,
        router: {
            route,
            setRoute,
        },
        createWorkout,
        setJournal: (
            value: (
                prevState: server.journal.day.Journal.output,
            ) => server.journal.day.Journal.output,
        ) => {
            props.setResolved((prevState) => ({
                journal: value(prevState.journal),
            }));
        },
        restTimer,
        startRestTimer,
    };
}

const JournalTab = (props: Props) => {
    const {day, profile, hasNoMeals, activities} = props;

    const [showAnimatedInstagram, setShowAnimatedInstagram] = React.useState(false);
    const isAskingForRating = useAppSelector((state) => state.scenes.isAskingForRating);

    const canShowInstagram = getFollowOnInstagramVisibility(props.profile);
    const canShowMyFitnessPal = getMyFitnessPalVisibility(props.profile);

    useTimeoutFn(() => {
        if (canShowInstagram === "VISIBLE") {
            void analytics.track("PROMPTED_FOLLOW_ON_INSTAGRAM");
        }
        setShowAnimatedInstagram(true);
    }, 2000);

    const getEntryLink = (entry: server.journal.nutrition.Entry.output) => {
        return router.locator({
            route: "update_journal_entry.macros",
            params: {
                key: props.day.key,
                entryUUID: entry.uuid,
            },
            resolved: props.resolved,
        });
    };

    const onDragEnd = (result: DropResult) => {
        if (!result.destination) {
            return;
        }

        const source = result.source;
        const destination = result.destination;

        if (
            source.droppableId === destination.droppableId &&
            source.index === destination.index
        ) {
            return;
        }

        if (result.type === constants.DROPPABLE_TYPES.MEAL) {
            const updatedDay = journal.swapMeal(
                props.day,
                source.index,
                destination.index,
            );

            props.setResolved((prevState) => ({
                journal: {
                    ...prevState.journal,
                    day: updatedDay,
                },
            }));
            void props.dispatch(journal.sortMealsAndEntries(updatedDay, "meals"));
        } else if (result.type === constants.DROPPABLE_TYPES.ENTRY) {
            const updatedDay = journal.swapEntry(
                props.day,
                source.index,
                source.droppableId as UUID,
                destination.index,
                destination.droppableId as UUID,
            );
            props.setResolved((prevState) => ({
                journal: {
                    ...prevState.journal,
                    day: updatedDay,
                },
            }));

            void props.dispatch(journal.sortMealsAndEntries(updatedDay, "entries"));
        }
    };

    const rendered = hasNoMeals ? (
        <>
            <div className={classNames(styles.empty, "py-4")}>
                <graphics.EmptyJournal title="Empty journal" className="max-sm:h-32!" />
                <div className="leading-6">
                    {props.calendar != null && (
                        <>
                            <h3 className="text-lg">
                                {" "}
                                No meals for {props.calendar.formattedDay}
                            </h3>
                        </>
                    )}
                </div>
            </div>
        </>
    ) : (
        <div>
            <DragDropContext
                onDragStart={() => hardware.haptic()}
                onDragEnd={onDragEnd}
            >
                <Droppable droppableId="droppable" type="MEAL">
                    {(provided) => (
                        <div {...provided.droppableProps} ref={provided.innerRef}>
                            {journal
                                .prepareJournalRows(day, activities)
                                .map((meal, index) =>
                                    meal.type == "ACTIVITY" ? (
                                        <ActivityCard
                                            key={meal.activity.apple_health_uuid}
                                            activity={meal}
                                        />
                                    ) : (
                                        <Draggable
                                            key={meal.meal.uuid}
                                            draggableId={meal.meal.uuid.toString()}
                                            index={index}
                                        >
                                            {(innerProvided, innerSnapshot) => (
                                                <div
                                                    ref={innerProvided.innerRef}
                                                    {...innerProvided.draggableProps}
                                                >
                                                    <MealCard
                                                        sorting={{
                                                            handle: innerProvided.dragHandleProps,
                                                            isDragging:
                                                                innerSnapshot.isDragging,
                                                        }}
                                                        hiddenMacros={
                                                            profile.has_hidden_macros
                                                        }
                                                        links={{
                                                            mealLink: router.locator({
                                                                route: "update_meal.index",
                                                                params: {
                                                                    key: props.day.key,
                                                                    mealUUID:
                                                                        meal.meal.uuid,
                                                                },
                                                                resolved:
                                                                    props.resolved,
                                                            }),
                                                            getEntryLink,
                                                        }}
                                                        meal={meal.meal}
                                                    />
                                                </div>
                                            )}
                                        </Draggable>
                                    ),
                                )}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </div>
    );

    return (
        <div className="flex flex-1 flex-col">
            {[].length == 1 && props.calendar != null && (
                <WeekDatePicker
                    currentDate={props.calendar.dateString}
                    onSelect={(selectedDate) => {
                        void hardware.haptic();
                        void router.push(`journal.${props.currentTab}`, {
                            key: selectedDate,
                        });
                    }}
                />
            )}
            <div className="mb-joy-step">
                <StepsAndWeight
                    isMealPlan={props.calendar == null}
                    profile={props.profile}
                    journal={props.resolved.journal}
                    setJournal={(updated) => {
                        props.setResolved(() => ({
                            journal: updated,
                        }));
                    }}
                />
            </div>
            <Habits
                isMealPlan={props.calendar == null}
                profile={props.profile}
                journal={props.resolved.journal}
                setJournal={(updated) => {
                    props.setResolved(() => ({
                        journal: updated,
                    }));
                }}
            >
                {canShowMyFitnessPal === "VISIBLE" && (
                    <MigrateFroMyFitnessPal {...props} />
                )}
                <AppleHealthResyncCard />
                <CheckIn {...props} />
            </Habits>
            {props.calendar != null && (
                <AnimatePresence mode="popLayout">
                    {showAnimatedInstagram &&
                    isAskingForRating == false &&
                    canShowInstagram === "VISIBLE" ? (
                        <motion.div
                            key="followOnInstagramAnimated"
                            initial={{opacity: 0, x: 100, scale: 1}}
                            animate={{
                                opacity: 1,
                                x: 0,
                                scale: [1],
                                transition: {duration: 0.3},
                            }}
                            exit={{
                                opacity: 0,
                                x: -100,
                                scale: 1,
                                transition: {duration: 0.3},
                            }}
                        >
                            <FollowOnInstagram {...props} />
                        </motion.div>
                    ) : (
                        <motion.div
                            key="workoutCardAnimated"
                            initial={{opacity: 1, x: 0}}
                            animate={{opacity: 1, x: 0, transition: {duration: 0.3}}}
                            exit={{opacity: 0, x: -100, transition: {duration: 0.3}}}
                        >
                            <WorkoutCard
                                createWorkout={props.createWorkout}
                                profile={props.profile}
                                journal={props.resolved.journal}
                                updateJournal={(updated) => {
                                    props.setResolved(() => ({
                                        journal: updated,
                                    }));
                                }}
                                switchTab={props.switchTab}
                                setWorkoutRoute={props.setWorkoutRoute}
                                calendar={{
                                    todayString: props.calendar.todayString,
                                    date: dates.formatISO(props.calendar.date, {
                                        representation: "date",
                                    }),
                                }}
                            />
                        </motion.div>
                    )}
                </AnimatePresence>
            )}

            <div className="flex flex-1 flex-col">{rendered}</div>
        </div>
    );
};

const SummaryTab = (props: Props) => {
    const {targets} = props.resolved.journal;

    return (
        <Gradient>
            <div className="flex flex-col gap-2.5">
                {props.profile.user.is_client == false && <Onboarding />}
                <DayForm
                    {...props}
                    target={props.day.target}
                    targets={targets}
                    day={props.resolved.journal.day}
                />
            </div>
        </Gradient>
    );
};

export type JournalImplementation = ReturnType<Conditions<Props, Props["currentTab"]>>;

// The date carousel is a header bubble: the same glass as the menu and streak.
const JournalTitle = (props: {children: React.ReactNode}) => (
    <div className="flex justify-center">
        <HeaderSurface className="rounded-full">{props.children}</HeaderSurface>
    </div>
);

export const Journal = router.Journal.useConditions((_props): JournalImplementation => {
    const props = useJournalHooks(_props);
    const hideSetup = useAppSelector((state) => state.scenes.hideSetup);
    const {meal_plan, profile, useKilojoules} = props;

    if (props.profile.user.casted_dismissed_announcements.includes("LEARNING_WEEK")) {
        return useLearningWeek(props);
    }

    if (isWalkingClubBoxed(props.profile)) {
        return useWalkingClubBox();
    }

    if (props.profile.user.casted_dismissed_announcements.includes("PLAN_WAITLIST")) {
        return usePlanWaitlist(props);
    }

    const journalTab = (
        <>
            <JournalTab {...props} />
        </>
    );

    if (props.calendar == null) {
        return {
            view: journalTab,
            actions: [],
            options: {
                scene: {kind: "SIMPLE"},
                bottomSection: <BottomSection {...props} />,
                title: props.title,
                back: {
                    title: "Meal Plan",
                    onClick: () => {
                        window.location.assign(
                            reverse("meal_plan_detail", {
                                meal_plan_id: props.resolved.journal.day.id,
                            }),
                        );
                    },
                },
            },
        };
    }
    const titleSection = (
        <JournalTitle>
            <DateCarousel
                bare
                previousDayLink={router.locator({
                    route: `journal.${props.currentTab}`,
                    params: {key: props.calendar.previous},
                    type: "back",
                })}
                onDateClick={() =>
                    props.dispatch(
                        journal.pickDateAndGoToDay(props.day.key, props.currentTab),
                    )
                }
                nextDayLink={router.locator({
                    route: `journal.${props.currentTab}`,
                    params: {key: props.calendar.next},
                    type: "forward",
                })}
                date={props.calendar.dateString}
                restTimer={{
                    seconds:
                        props.restTimer == -1
                            ? null
                            : Object.is(props.restTimer, -0)
                              ? 0
                              : props.restTimer,
                    setRestTimer: props.startRestTimer,
                }}
            />
        </JournalTitle>
    );

    if (meal_plan != null) {
        return {
            view: (
                <Flush>
                    {/* Fake TabBar */}
                    <div style={{marginTop: 10}}>
                        <TabBar
                            tabs={[
                                {
                                    icon: "custom-0283-contacts",
                                    link: () => {},
                                    title: "Your Meal Plan",
                                    isSelected: true,
                                    hasErrors: false,
                                },
                            ]}
                            showSingleTab
                        />
                    </div>
                    {meal_plan.meals.map((meal) => (
                        <div key={meal.uuid}>
                            <MealCard
                                meal={meal}
                                hiddenMacros={profile.has_hidden_macros}
                                defaultExpanded={true}
                            />
                        </div>
                    ))}
                    <div className="h-1"></div>
                    {profile.has_hidden_macros === false && (
                        <DaySummary
                            useKilojoules={useKilojoules}
                            target={props.day.target}
                            day={meal_plan}
                        />
                    )}
                </Flush>
            ),
            actions: [
                {
                    icon: "save",
                    title: "Accept",
                    onClick: async () => {
                        const response = await server.journal.day.accept_meal_plan({
                            key: props.resolved.journal.day.key,
                            user_id: props.currentlyManagedUserId,
                        });
                        if (response.type === "success") {
                            await props.reloadData();
                            props.notify(
                                "Meal plan accepted for today. You can now add or edit entries to your journal.",
                                {type: "info"},
                            );
                            void analytics.track("ACCEPTED_MEAL_PLAN");
                        }
                    },
                },
                {
                    icon: "remove",
                    title: "Clear",
                    onClick: async () => {
                        const response = await server.journal.day.reject_meal_plan({
                            key: props.resolved.journal.day.key,
                            user_id: props.currentlyManagedUserId,
                        });
                        if (response.type === "success") {
                            await props.reloadData();
                            props.notify(
                                "Meal plan rejected for today. You can add or remove entries manually.",
                                {type: "info"},
                            );
                            void analytics.track("REJECTED_MEAL_PLAN");
                        }
                    },
                },
            ],
            options: {
                scene: {kind: "SIMPLE"},
                title: props.title,
                titleSection,
            },
        };
    }

    if (props.currentTab == "journal") {
        const showWelcome =
            window.cordova == null &&
            props.profile.user.casted_dismissed_announcements.includes(
                "SHOW_RECIPE_REGISTRATION",
            ) === true &&
            props.profile.user.casted_dismissed_announcements.includes(
                "HIDE_RECIPE_REGISTRATION",
            ) === false &&
            hideSetup === false;

        const welcome = <Welcome {...props} />;

        return {
            view: <Gradient>{showWelcome ? welcome : journalTab}</Gradient>,
            actions: [],
            options: {
                bottomSection: showWelcome ? null : <BottomSection {...props} />,
                scene: {
                    kind: "SIMPLE",
                    surface: {kind: "custom", background: JOY_GRADIENT_TOP},
                },
                title: props.title,
                titleSection,
                more: <Streak value={props.resolved.journal.streak} />,
                onRefresh: async () => {
                    const response = await server.journal.day.journal({
                        key: props.day.key,
                        user_id: props.day.user_id,
                    });
                    if (response.type === "success") {
                        props.setResolved(() => ({journal: response.data}));
                    }
                },
                topSection: props.pushPrompt,
            },
        };
    }

    if (props.currentTab == "summary") {
        const tiles: router.PillAction[] = [
            {
                icon: "0395-calendar-insert",
                title: "Move",
                onClick: () =>
                    props.dispatch(
                        journal.pickDateAndMoveDay(props.resolved.journal.day),
                    ),
            },
            {
                icon: "copy",
                title: "Copy",
                onClick: () =>
                    props.dispatch(
                        journal.pickDateAndCopyDay(props.resolved.journal.day),
                    ),
            },
            {
                icon: "share",
                title: "Share",
                onClick: async () => {
                    const response = await server.journal.nutrition.publish_day({
                        date: props.day.key,
                        user_id: props.currentlyManagedUserId,
                    });

                    if (response.type === "success") {
                        const outcome = await shareLink(
                            response.data.share_uuid,
                            props.title,
                        );
                        if (outcome === "copied") {
                            props.notify("Link copied.");
                        }
                    }
                },
            },
        ];
        const save: router.PillAction = {
            icon: "save",
            title: "Save",
            onClick: () => {
                void server.journal.day.update_day(
                    {user_id: props.day.user_id, key: props.day.key},
                    {...props.day, deleted_meal_uuids: []},
                );

                void analytics.track("UPDATE_DAY_SUCCESS");
                props.notify("Day updated.");
            },
        };
        return {
            view: <SummaryTab {...props} />,
            actions: [],
            options: {
                back: {
                    title: "Journal",
                    onClick: () => {
                        void props.switchTab("journal");
                    },
                },
                scene: {
                    kind: "SIMPLE",
                    surface: {kind: "custom", background: JOY_GRADIENT_TOP},
                    bottom: {kind: "pills", primary: save, tiles},
                },
                more: <Streak value={props.resolved.journal.streak} />,
                title: props.title,
                titleSection,
            },
        };
    }
    return {
        view: <JournalWorkout {...props} startRestTimer={props.startRestTimer} />,
        actions: [],
        options: {
            back: {
                title: "Journal",
                onClick: () => {
                    void props.switchTab("journal", {type: "back"});
                },
            },
            scene: {kind: "SIMPLE"},
            title: props.title,
            titleSection,
            // Guided workouts portal their menu here; free-form ones use the bar.
            more: <div id="more"></div>,
            bottomSection: <WorkoutBarSlot workout={props.resolved.journal.workout} />,
        },
    };
});

export const Today = router.Today.useRedirect((props) => {
    const date = dates.formatISO(new Date(), {representation: "date"});

    return router.locator({
        route: `journal.${props.currentTab}`,
        params: {
            key: date,
        },
    });
});
